import {
  Alert,
  App,
  Button,
  Checkbox,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Spin,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import type File from '../../../interfaces/File';
import type {
  CreateFilePublicShareLinkRequest,
  PublicFileShareLinkSettings,
} from '../../../interfaces/FileApiService';
import type { FilePublicShareLinkItem } from '../../../interfaces/FilePublicShareLinkItem';
import { FileService } from '../../../services/fileApiService';
import { ticksToDateSafe } from '../../../utils/dateConversion';
import { copyToClipboard, dateToTicks, getFilePublicLink } from '../../../utils/utils';
import { Icon } from '../../Core/Icon';
import { publicLinkExpirationDayjs } from './publicLinkExpirationDayjs';

const fileService = new FileService();

export interface FilePublicLinkFormValues {
  canDownload: boolean;
  expirationAt: dayjs.Dayjs | null;
  maxAccessCount: number | null;
  protectWithPassword: boolean;
  accessCode: string;
}

type FilePublicLinkStep = 'loading' | 'create' | 'detail';

interface FilePublicLinkFormProps {
  file: File;
  onSubmit?: (payload: CreateFilePublicShareLinkRequest) => void | Promise<void>;
  onCancel: () => void;
  onSuccess?: () => void;
  /** Called after a new public link is created (so the parent can set file.isPublicShare = true). */
  onCreated?: () => void;
  /** Called after the public link is revoked (so the parent can set file.isPublicShare = false). */
  onRevoked?: () => void;
  isLoading?: boolean;
}

/**
 * FilePublicLinkForm component
 *
 * Enables users to create, view, and revoke a public share link for a file.
 * Handles three states:
 *   1. loading - Fetching link information from the server.
 *   2. create  - Form for creating a new link if one does not exist.
 *   3. detail  - Displays details for an existing link, and allows revocation.
 *
 * Props:
 *   - file: The file for which to manage the public link.
 *   - onCancel: Callback when the modal or UI is closed.
 *   - onSuccess: Callback to trigger after successful create/revoke.
 *   - isLoading: External loading state for actions.
 */
export const FilePublicLinkForm = ({
  file,
  onCancel,
  onSuccess,
  onCreated,
  onRevoked,
  isLoading: externalLoading = false,
}: FilePublicLinkFormProps) => {
  const { t } = useTranslation();
  const { message, notification } = App.useApp();
  const [form] = Form.useForm<FilePublicLinkFormValues>();
  const [protectWithPassword, setProtectWithPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [step, setStep] = useState<FilePublicLinkStep>('loading');
  const [detail, setDetail] = useState<FilePublicShareLinkItem | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [publicFileShareLinkSettings, setPublicFileShareLinkSettings] =
    useState<PublicFileShareLinkSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  /**
   * Fetches the public link details for the current file.
   * - If found, sets detail state and moves to 'detail' step
   * - If not found, clears detail and moves to 'create' step
   * - If error, displays fetch error and moves to 'create' step
   */
  const fetchPublicLink = useCallback(async () => {
    setFetchError(null);
    fileService
      .getPublicFileShareLink(file.id)
      .then((data) => {
        if (data == null) {
          setDetail(null);
          setStep('create');
        } else {
          setDetail(data);
          setStep('detail');
        }
      })
      .catch((error) => {
        const status = error?.status ?? error?.response?.status;
        if (status === 404) {
          setDetail(null);
          setStep('create');
        } else {
          setFetchError(t('errors.genericError'));
          setStep('create');
        }
      });
  }, [file.id, t]);

  /**
   * When form values change, check if 'protectWithPassword' changed.
   * If un-setting, clear the 'accessCode' field.
   */
  const handleValuesChange = useCallback(
    (changed: Partial<FilePublicLinkFormValues>) => {
      if ('protectWithPassword' in changed) {
        setProtectWithPassword(Boolean(changed.protectWithPassword));
        if (!changed.protectWithPassword) form.setFieldValue('accessCode', '');
      }
    },
    [form],
  );

  /**
   * Handle submission to create the public share link.
   * - Validates fields
   * - Constructs API payload
   * - Calls service
   * - Handles error codes and UI updates accordingly
   */
  const handleCreateSubmit = useCallback(async () => {
    flushSync(() => setSubmitting(true));
    try {
      const values = (await form.validateFields()) as FilePublicLinkFormValues;
      const payload: CreateFilePublicShareLinkRequest = {
        accessCode:
          values.protectWithPassword && values.accessCode?.trim() ? values.accessCode.trim() : null,
        canDownload: values.canDownload ?? true,
        expirationAt: values.expirationAt
          ? dateToTicks(values.expirationAt.startOf('day').toDate())
          : null,
        fileId: file.id,
        maxAccessCount:
          values.maxAccessCount != null && values.maxAccessCount > 0 ? values.maxAccessCount : null,
      };
      await fileService.createPublicFileShareLink(payload);
      await fetchPublicLink();
      onCreated?.();
      onSuccess?.();
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      const apiError = (error as { response?: string })?.response;

      if (status === 403) {
        notification.error({
          message:
            apiError === ApiErrorEnum.PublicLinkSettingDisabled
              ? t('modal.filePublicLinkForm.errors.featureDisabled')
              : t('modal.filePublicLinkForm.errors.notOwner'),
        });
        return;
      }

      switch (apiError) {
        case ApiErrorEnum.PublicLinkSettingDisabled:
          notification.error({ message: t('modal.filePublicLinkForm.errors.featureDisabled') });
          break;
        case ApiErrorEnum.PublicLinkAlreadyExists:
          notification.warning({ message: t('modal.filePublicLinkForm.errors.linkAlreadyExists') });
          break;
        case ApiErrorEnum.PublicLinkClassificationRequired:
          notification.error({ message: t('errors.publicLinkClassificationRequired') });
          break;
        case ApiErrorEnum.PublicLinkPasswordRequired:
          notification.error({ message: t('errors.publicLinkPasswordRequired') });
          break;
        case ApiErrorEnum.PublicLinkMaxAccessCountRequired:
          notification.error({ message: t('errors.publicLinkMaxAccessCountRequired') });
          break;
        case ApiErrorEnum.PublicLinkExpirationRequired:
          notification.error({ message: t('errors.publicLinkExpirationRequired') });
          break;
        case ApiErrorEnum.PublicLinkExpirationExceeded:
          notification.error({ message: t('errors.publicLinkExpirationExceeded') });
          break;
        case ApiErrorEnum.PublicLinkExpirationInPast:
          notification.error({ message: t('errors.publicLinkExpirationInPast') });
          break;
        case ApiErrorEnum.PublicLinkFolderPasswordProtected:
          notification.error({ message: t('errors.publicLinkFolderPasswordProtected') });
          break;
        case ApiErrorEnum.PublicLinkDownloadRestricted:
          notification.error({ message: t('errors.publicLinkDownloadRestricted') });
          break;
        case ApiErrorEnum.PublicLinkAnticaptureActive:
          notification.error({ message: t('errors.publicLinkAnticaptureActive') });
          break;
        case ApiErrorEnum.PublicLinkExternalSharingRestricted:
          notification.error({ message: t('errors.publicLinkExternalSharingRestricted') });
          break;
        case ApiErrorEnum.PublicLinkResharingDenied:
          notification.error({ message: t('errors.publicLinkResharingDenied') });
          break;
        case ApiErrorEnum.PublicLinkDownloadTagsRestricted:
          notification.error({ message: t('errors.publicLinkDownloadTagsRestricted') });
          break;
        case ApiErrorEnum.PublicLinkApprovalRequired:
          notification.error({ message: t('errors.publicLinkApprovalRequired') });
          break;
        case ApiErrorEnum.PublicLinkMandatoryExpiration:
          notification.error({ message: t('errors.publicLinkMandatoryExpiration') });
          break;
        case ApiErrorEnum.AntivirusFileInfected:
          notification.error({ message: t('errors.antivirusFileInfected') });
          break;
        case ApiErrorEnum.AntivirusNotAvailable:
          notification.error({ message: t('errors.antivirusNotAvailable') });
          break;
        case ApiErrorEnum.AntivirusRescanNotAllowed:
          notification.error({ message: t('errors.antivirusRescanNotAllowed') });
          break;
        default:
          notification.error({
            message: t('errors.genericCreateError', { resource: t('common.link') }),
          });
      }
    } finally {
      setSubmitting(false);
    }
  }, [file.id, form, fetchPublicLink, notification, t, onCreated, onSuccess]);

  /**
   * Handles revoking the current public share link.
   * - Calls API
   * - Resets state and moves to 'create' step on success
   * - Handles server error codes
   */
  const handleRevoke = useCallback(async () => {
    flushSync(() => setRevoking(true));
    try {
      await fileService.deletePublicFileShareLink(file.id);
      message.success(t('modal.filePublicLinkForm.revoked'));
      setDetail(null);
      setStep('create');
      onRevoked?.();
      onSuccess?.();
    } catch (err: unknown) {
      // Handle not found and forbidden errors
      const status = (err as { response?: { status?: number; data?: { error?: string } } })
        ?.response?.status;
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (status === 404 && code === ApiErrorEnum.LinkNotFound) {
        message.warning(t('modal.filePublicLinkForm.errors.linkNotFound'));
        setStep('create');
        setDetail(null);
      } else if (status === 403) {
        notification.error({ message: t('modal.filePublicLinkForm.errors.notOwner') });
      } else {
        notification.error({
          message: t('errors.genericUpdateError', { resource: t('common.link') }),
        });
      }
    } finally {
      setRevoking(false);
    }
  }, [file.id, message, notification, t, onRevoked, onSuccess]);

  /**
   * Copies the public URL to the clipboard and shows a success message.
   */
  const handleCopyLink = useCallback(
    (url: string) => {
      copyToClipboard(url);
      message.success(t('common.linkCopied'));
    },
    [message, t],
  );

  // On mount and when file changes, fetch link details.
  useEffect(() => {
    fetchPublicLink();
  }, [fetchPublicLink]);

  /**
   * When in create step, fetch public file share link settings (limits and required fields).
   * Form shows loading until settings are loaded.
   */
  useEffect(() => {
    if (step !== 'create') {
      setPublicFileShareLinkSettings(null);
      return;
    }
    let cancelled = false;
    setSettingsLoading(true);
    fileService
      .getPublicFileShareLinkSettings(file.id)
      .then((data) => {
        if (!cancelled) {
          setPublicFileShareLinkSettings(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPublicFileShareLinkSettings(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSettingsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [step, file.id]);

  /**
   * Reset form fields and UI state when entering the 'create' step,
   * or when file changes. Pre-fills expiration and maxAccessCount from settings when mandatory.
   */
  useEffect(() => {
    if (step === 'create') {
      const forcePassword = publicFileShareLinkSettings?.settings?.forcePassword ?? false;
      const mandatoryMax = publicFileShareLinkSettings?.settings?.mandatoryMaxAccessCount ?? 0;
      const maxDays = publicFileShareLinkSettings?.settings?.maxExpirationDays;
      const defaultExpiration =
        maxDays != null && maxDays > 0 ? publicLinkExpirationDayjs(maxDays) : null;
      const defaultMaxCount = mandatoryMax > 0 ? mandatoryMax : null;
      form.setFieldsValue({
        accessCode: '',
        canDownload: true,
        expirationAt: defaultExpiration,
        maxAccessCount: defaultMaxCount,
        protectWithPassword: forcePassword,
      });
      setProtectWithPassword(forcePassword);
    }
  }, [file.id, step, form, publicFileShareLinkSettings?.settings]);

  /**
   * When public link settings load in create step, apply forcePassword so checkbox and password field stay in sync.
   */
  useEffect(() => {
    if (step === 'create' && publicFileShareLinkSettings?.settings?.forcePassword) {
      form.setFieldsValue({ protectWithPassword: true });
      setProtectWithPassword(true);
    }
  }, [step, publicFileShareLinkSettings?.settings?.forcePassword, form]);

  // --- UI RENDER BRANCHES (step state) ---

  // Loading spinner while fetching public link info
  if (step === 'loading' && !fetchError && !detail) {
    return (
      <div className="flex min-h-[200px] items-center justify-center py-8">
        <Spin size="large" />
      </div>
    );
  }

  // Show fetch error, offering retry and cancel
  if (fetchError) {
    return (
      <div className="py-4">
        <p className="text-red">{fetchError}</p>
        <div className="mt-4 flex gap-2">
          <Button onClick={fetchPublicLink}>{t('common.retry')}</Button>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
        </div>
      </div>
    );
  }

  // Show share link details and option to revoke if active
  if (step === 'detail' && detail) {
    const createdDate = ticksToDateSafe(detail.createdAt ?? undefined);
    const expiresDate = detail.expirationAt != null ? ticksToDateSafe(detail.expirationAt) : null;
    const publicUrl = getFilePublicLink(detail.publicToken);
    // Compute status label (revoke is always allowed when a link exists)
    const statusLabel = detail.isExhausted
      ? t('modal.filePublicLinkForm.statusExhausted')
      : detail.isExpired
        ? t('modal.filePublicLinkForm.statusExpired')
        : t('modal.filePublicLinkForm.statusActive');

    return (
      <div className="flex min-w-0 flex-col gap-4 py-2">
        <Descriptions
          column={1}
          size="small"
          bordered
          className="[&_.ant-descriptions-item-content]:min-w-0 [&_.ant-descriptions-item-content]:overflow-hidden [&_.ant-descriptions-item-label]:w-36 [&_.ant-descriptions-view_table]:w-full [&_.ant-descriptions-view_table]:table-fixed"
        >
          <Descriptions.Item label={t('modal.filePublicLinkForm.publicUrl')}>
            <div className="flex min-w-0 items-center gap-2">
              <Input variant="borderless" className="px-0!" value={publicUrl} readOnly />
              {/* <span
                className="min-w-0 flex-1 truncate font-mono text-sm"
                title={publicUrl}
              >
                {publicUrl}
              </span> */}
              <Tooltip title={t('modal.filePublicLinkForm.copyLink')}>
                <Button
                  type="text"
                  size="small"
                  className="shrink-0"
                  aria-label={t('a11y.copyLink')}
                  onClick={() => handleCopyLink(publicUrl)}
                >
                  <Icon name="CopyIcon" className="h-4 w-4" />
                </Button>
              </Tooltip>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('modal.filePublicLinkForm.status')}>
            {statusLabel}
          </Descriptions.Item>
          <Descriptions.Item label={t('modal.filePublicLinkForm.expiresAt')}>
            {detail.expirationAt != null
              ? dayjs(expiresDate).format('DD/MM/YYYY')
              : t('modal.filePublicLinkForm.expirationPlaceholder')}
          </Descriptions.Item>
          <Descriptions.Item label={t('modal.filePublicLinkForm.remainingAccesses')}>
            {detail.maxAccessCount != null
              ? `${detail.accessCount} / ${detail.maxAccessCount}`
              : t('common.unlimited')}
          </Descriptions.Item>
          <Descriptions.Item label={t('modal.filePublicLinkForm.hasPassword')}>
            {detail.hasPassword ? t('common.yes') : t('common.no')}
          </Descriptions.Item>
          <Descriptions.Item label={t('modal.filePublicLinkForm.createdAt')}>
            {createdDate ? dayjs(createdDate).format('DD/MM/YYYY HH:mm:ss') : '-'}
          </Descriptions.Item>
        </Descriptions>
        <p className="text-gray-dark text-sm">{t('modal.filePublicLinkForm.warning')}</p>

        <div className="flex justify-between gap-2">
          <Button
            danger
            onClick={handleRevoke}
            loading={externalLoading || revoking}
            disabled={externalLoading || revoking}
          >
            {t('modal.filePublicLinkForm.revoke')}
          </Button>
          <Button onClick={onCancel}>{t('common.close')}</Button>
        </div>
      </div>
    );
  }

  // Create step: show loading while fetching public link settings
  if (step === 'create' && settingsLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center py-8">
        <Spin size="large" />
      </div>
    );
  }

  // Derive constraints from public link settings (only in create step, after loading)
  const settings = publicFileShareLinkSettings?.settings;
  const forcePassword = settings?.forcePassword ?? false;
  const mandatoryMaxAccessCount = settings?.mandatoryMaxAccessCount ?? 0;
  const maxExpirationDays = settings?.maxExpirationDays;

  const expirationMaxDate =
    maxExpirationDays != null && maxExpirationDays > 0
      ? publicLinkExpirationDayjs(maxExpirationDays)
      : undefined;

  const maxAccessCountMin = mandatoryMaxAccessCount > 0 ? 1 : 0;
  const maxAccessCountMax = mandatoryMaxAccessCount > 0 ? mandatoryMaxAccessCount : undefined;
  const maxAccessCountRequired = mandatoryMaxAccessCount > 0;
  const expirationRequired = (maxExpirationDays ?? 0) > 0;

  const defaultExpirationAt = expirationRequired && expirationMaxDate ? expirationMaxDate : null;
  const defaultMaxAccessCount = mandatoryMaxAccessCount > 0 ? mandatoryMaxAccessCount : null;

  const showPasswordField = protectWithPassword || forcePassword;

  const expirationLabelKey = expirationRequired
    ? 'modal.filePublicLinkForm.expirationLabelRequired'
    : 'modal.filePublicLinkForm.expirationLabel';
  const maxAccessCountLabelKey = maxAccessCountRequired
    ? 'modal.filePublicLinkForm.maxAccessCountLabelRequired'
    : 'modal.filePublicLinkForm.maxAccessCountLabel';

  // If no link exists or user is creating, show the creation form.
  return (
    <Form
      form={form}
      layout="vertical"
      aria-label={t('a11y.filePublicLinkForm')}
      className="pt-4! [&_.ant-form-item]:mb-4!"
      onValuesChange={handleValuesChange}
      initialValues={{
        accessCode: '',
        canDownload: true,
        expirationAt: defaultExpirationAt,
        maxAccessCount: defaultMaxAccessCount,
        protectWithPassword: forcePassword,
      }}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Form.Item
          name="expirationAt"
          label={t(expirationLabelKey)}
          rules={
            expirationRequired
              ? [{ message: t('errors.publicLinkExpirationRequired'), required: true }]
              : undefined
          }
        >
          <DatePicker
            className="w-full!"
            format="YYYY-MM-DD"
            minDate={dayjs()}
            maxDate={expirationMaxDate}
            placeholder={t('modal.filePublicLinkForm.expirationPlaceholder')}
          />
        </Form.Item>
        <Form.Item
          name="maxAccessCount"
          label={t(maxAccessCountLabelKey)}
          rules={
            maxAccessCountRequired
              ? [
                  {
                    message: t('modal.filePublicLinkForm.maxAccessCountPlaceholder'),
                    required: true,
                  },
                  {
                    max: mandatoryMaxAccessCount,
                    message: t('modal.filePublicLinkForm.maxAccessCountPlaceholder'),
                    min: 1,
                    type: 'number',
                  },
                ]
              : undefined
          }
        >
          <InputNumber
            className="w-full!"
            min={maxAccessCountMin}
            max={maxAccessCountMax}
            placeholder={t('modal.filePublicLinkForm.maxAccessCountPlaceholder')}
          />
        </Form.Item>
      </div>
      <Form.Item name="canDownload" valuePropName="checked">
        <Checkbox>{t('modal.filePublicLinkForm.allowDownload')}</Checkbox>
      </Form.Item>
      <Divider titlePlacement="start">{t('common.password')}</Divider>
      <Form.Item name="protectWithPassword" valuePropName="checked">
        <Checkbox disabled={forcePassword}>
          {t('modal.filePublicLinkForm.protectWithPassword')}
        </Checkbox>
      </Form.Item>
      {showPasswordField && (
        <Form.Item
          name="accessCode"
          label={t('modal.filePublicLinkForm.passwordLabel')}
          rules={[{ message: t('modal.filePublicLinkForm.passwordRequired'), required: true }]}
        >
          <Input.Password
            placeholder={t('modal.filePublicLinkForm.passwordPlaceholder')}
            autoComplete="off"
          />
        </Form.Item>
      )}
      <Alert title={t('modal.filePublicLinkForm.warning')} type="warning" />

      <div className="mt-4 flex justify-end gap-2">
        <Button onClick={onCancel} disabled={externalLoading || submitting}>
          {t('common.cancel')}
        </Button>
        <Button
          type="primary"
          loading={externalLoading || submitting}
          disabled={externalLoading || submitting}
          onClick={handleCreateSubmit}
        >
          {t('modal.filePublicLinkForm.createLink')}
        </Button>
      </div>
    </Form>
  );
};
